import { Pagination, Skeleton } from 'antd';
import { useAuthContext } from 'contexts/auth.context';
import { useGetOrderDetailsById } from 'lib/hooks/order/order.queries';

import { paths } from 'config/paths';
import { QueryData } from 'lib/api/utils';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { UserRole } from 'types/shared';
import { BackArrowIcon } from '../../components/icons';
import SidebarNavigation from '../SidebarNavigation/component';
import TopNavigation from '../TopNavigation/container';
import './styles.scss';

function OrderDetails(props: any) {
  const history = useHistory();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const { id: orderId } = useParams<{ id: string }>();

  const { user } = useAuthContext();

  const {
    data: orderDetailsData,
    isLoading,
    isPreviousData,
    isInitialLoading,
  } = useGetOrderDetailsById({
    orderId,
    page,
    limit,
  });

  const orderDetail = orderDetailsData?.order;
  const orderSimsCount = orderDetail?.orderSimsCount;

  const isLoadingNewPage = isPreviousData || isLoading;
  const isZeroSims = orderDetail?.sims.length === 0;
  const orderSims = orderDetail?.sims ?? [];

  useEffect(() => {
    if (user?.role === UserRole.USER) history.push('/dashboard');
  }, [history, user]);

  useEffect(() => {
    document.title = `Order ${orderDetail?.ordernumber ?? ''} Detail | UltraIoT`;
  }, [orderDetail]);

  const orderTypeFromLocationState = location.state ?? 'spr';

  const goToAvailableSimsPage = () => {
    if (orderTypeFromLocationState === 'spr') {
      history.push(paths.availableSims.getHref());
    } else if (orderTypeFromLocationState === 'eSIM') {
      history.push(paths.availableESims.getHref());
    } else {
      history.push(paths.fixedNeverBeenActivated.getHref());
    }
  };

  const renderActivateButton = () => {
    if (isInitialLoading)
      return (
        <Skeleton.Button
          active
          style={{ height: 50, borderRadius: 4, width: 140 }}
        />
      );
    if (isZeroSims) return null;

    return (
      <button
        onClick={goToAvailableSimsPage}
        className="btn btn-purple w-180 m-r-10"
      >
        {orderTypeFromLocationState === 'eSIM'
          ? 'Activate eSIMs'
          : 'Activate SIMs'}
      </button>
    );
  };

  const renderList = () => {
    if (isLoadingNewPage) return <OrderSimsListSkeleton count={15} />;

    if (isZeroSims) return <EmptyState />;

    return <OrderSimsList sims={orderSims} />;
  };

  return (
    <div className="layout-has-sidebar">
      <SidebarNavigation {...props} />
      <TopNavigation {...props} />

      <main className="dashboard-layout-content">
        <div className="row align-items-md-center mb-4">
          <div className="col-md-4">
            <button onClick={() => history.goBack()} className="btn btn-back">
              <BackArrowIcon className="back-icon" /> Back
            </button>
          </div>
        </div>

        <div className="row m-b-15" style={{ alignItems: 'center' }}>
          <div className="col-md-5">
            <div className="side-heading">
              {isInitialLoading ? (
                <Skeleton.Input
                  active
                  style={{ width: 300, borderRadius: 10, height: 40 }}
                />
              ) : (
                <h4 className="title">
                  Order #{orderDetail?.ordernumber} SIMs
                </h4>
              )}
            </div>
          </div>

          <div className="col-md-7">
            <div className="text-right">{renderActivateButton()}</div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="card m-b-15">
              <div className="card-body p-0">
                <div className="orders-sim-info">
                  <h4 className="title col">ICCID</h4>

                  {renderList()}
                </div>
              </div>
            </div>

            {isInitialLoading ? (
              <Skeleton.Input
                active
                style={{
                  height: 32,
                  borderRadius: 4,
                  width: 250,
                  marginBottom: '3rem',
                }}
              />
            ) : (
              <Pagination
                className="mb-5"
                hideOnSinglePage
                onChange={(page) => setPage(page)}
                current={page}
                onShowSizeChange={(_current, size) => setLimit(size)}
                pageSize={limit}
                defaultCurrent={page}
                total={orderSimsCount}
                showSizeChanger={false}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default OrderDetails;

function OrderSimsList(props: {
  sims: QueryData<typeof useGetOrderDetailsById>['order']['sims'];
}) {
  const history = useHistory();
  const { sims } = props;

  const simListItems = sims.map((item, index) => {
    const isActive = item.ultraplan === 'IOTDVS' && item.planid > 0;

    return isActive ? (
      <li
        key={index}
        onClick={() => history.push(paths.simDetail.getHref(item.simnumber))}
      >
        <span className="btn-view-apn">{item.simnumber}</span>
      </li>
    ) : (
      <li key={index}>{item.simnumber}</li>
    );
  });

  return <ul className="orders-sim-list">{simListItems}</ul>;
}

function EmptyState() {
  return (
    <div className="table-responsive">
      <table className="table custom-table-secondary">
        <tbody>
          <tr>
            <td style={{ paddingLeft: 45 }}>No SIMs found.</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function OrderSimsListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <ul className="orders-sim-list">
      {[...Array(count)].map((_, index) => (
        <li key={index}>
          <Skeleton.Input
            active
            style={{
              width: 200,
              borderRadius: 10,
              marginTop: 13,
              marginBottom: 13,
            }}
          />
        </li>
      ))}
    </ul>
  );
}
